import React, { useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { DashboardLayout } from "./Dashboard";
import ProfileBanner from "@/components/ProfileBanner";
import { useLocation, useNavigate, useParams } from "react-router";
import TableWithSearch from "@/components/TableWithSearch";
import { patientTableHeaders } from "@/utils/tablesData";
import { useOrg } from "@/contexts/useOrg";
import { Eye, CalendarDays, Settings, Bell, History } from "lucide-react";
import { useGetData } from "@/hooks/useGetData";
import MyDialogContent from "@/components/MyDialogContent";
import { Dialog } from "@/components/ui/dialog";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import { Button } from "@/components/ui/button";
import AxiosInstance from "@/utils/AxiosInstance";
import { formatOrganizationTimezoneDisplay, getPromise, getPromiseToast, trimmedObject } from "@/utils/helpers";
import {
  capitalizeTheStartingCharacterFilter,
  PERSON_CAPITALIZE_FIELDS,
} from "@/constants/payloadFilters";
import {
  formatYmdHmInTimeZone,
  getNowLocalDateTimeInput,
  isLocalDateTimePastInTimeZone,
  joinLocalDateTime,
  localDateTimeInputToUtcIso,
  splitLocalDateTime,
  utcIsoToLocalDateTimeInput,
} from "@/utils/patientTimezoneDateTime";
import { ResetSchema } from "./ResetPassword";
import { CaretakerForm } from "./Caretakers";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  HoverTooltip,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import PatientHistoryTimeline from "@/components/patient/PatientHistoryTimeline";
import { formatUsTimezoneLabel } from "@/constants/usOrgTimezones";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

/** Parse quiet-hours `HH:mm` (24h) for display; supports `H:mm`. */
function parseHHMMForDisplay(hhmm: string): { h24: number; minute: number } | null {
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h24 = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  if (
    Number.isNaN(h24) ||
    Number.isNaN(minute) ||
    h24 < 0 ||
    h24 > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return { h24, minute };
}

/** Show e.g. `3:32 PM` while state/API remain `15:32`. */
function formatHHmmAs12h(hhmm: string) {
  const p = parseHHMMForDisplay(hhmm);
  if (!p) return "";
  const d = new Date(1970, 0, 1, p.h24, p.minute, 0, 0);
  return format(d, "h:mm a", { locale: enUS });
}

function parseLocalDatetimeValue(v: string): Date | undefined {
  if (!v?.trim()) return undefined;
  const { dateYmd, timeHm } = splitLocalDateTime(v);
  if (!dateYmd) return undefined;
  const d = new Date(`${dateYmd}T${timeHm}:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Stores `yyyy-MM-ddTHH:mm` in caretaker timezone; shows MM/DD/YYYY in the trigger label. */
export function SnoozeCustomDateTimeField({
  value,
  onChange,
  id,
  timeZone,
  disablePast,
}: {
  value: string;
  onChange: (localValue: string) => void;
  id?: string;
  timeZone?: string;
  disablePast?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const { dateYmd, timeHm } = splitLocalDateTime(value);
  const d = React.useMemo(() => parseLocalDatetimeValue(value), [value]);

  const tzNow = React.useMemo(() => {
    if (!disablePast || !timeZone?.trim()) return null;
    return formatYmdHmInTimeZone(new Date(), timeZone.trim());
  }, [disablePast, timeZone, open]);

  const isPastInTz = (local: string) =>
    isLocalDateTimePastInTimeZone(local, timeZone);

  const commit = (next: string) => {
    if (disablePast && isPastInTz(next)) {
      toast.error(
        "Please select current or future date and time in the caretaker's timezone.",
      );
      return;
    }
    onChange(next);
  };

  const mergeDateKeepingTime = (day: Date) => {
    const ymd = format(day, "yyyy-MM-dd", { locale: enUS });
    let nextHm = timeHm || "00:00";
    if (disablePast && tzNow && ymd === tzNow.ymd && nextHm < tzNow.hm) {
      nextHm = tzNow.hm;
    }
    commit(joinLocalDateTime(ymd, nextHm));
  };

  const mergeTimeKeepingDate = (nextTimeHm: string) => {
    if (!dateYmd) return;
    commit(joinLocalDateTime(dateYmd, nextTimeHm));
  };

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full justify-start font-normal"
          id={id}
        >
          <CalendarDays className="mr-2 size-4 opacity-70 shrink-0" />
          <span className="truncate">
            {d
              ? format(d, "MM/dd/yyyy h:mm a", { locale: enUS })
              : "Select date & time"}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[10001] w-auto overflow-hidden p-0"
        align="start"
      >
        <Calendar
          mode="single"
          locale={enUS}
          selected={d}
          disabled={
            disablePast && tzNow
              ? (day) => format(day, "yyyy-MM-dd") < tzNow.ymd
              : undefined
          }
          onSelect={(day) => {
            if (!day) return;
            mergeDateKeepingTime(day);
          }}
        />
        <div className="flex items-center gap-2 border-t p-3">
          <Label className="text-muted-foreground shrink-0">Time</Label>
          <input
            type="time"
            step={60}
            className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm h-9"
            value={timeHm}
            min={
              disablePast && tzNow && dateYmd === tzNow.ymd
                ? tzNow.hm
                : undefined
            }
            onChange={(e) => mergeTimeKeepingDate(e.target.value)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

const CaretakerProfile = () => {
  const { state } = useLocation();
  // console.log("xx", state);
  const [patients, setPatients] = React.useState([]);
  const navigate = useNavigate();
  const { selectedOrgId } = useOrg();

  const { id } = useParams();
  const [provider, setProvider] = useState(state?.data || {});
  const [password, setPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editOpen , setEditOpen] = useState(false)
  const {organizations} = useOrg()
  const [selectedOrgs, setSelectedOrgs] = useState([])
  const [notifOpen, setNotifOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);

  // const CARETAKER_TIMEZONES = [
  //   { label: "Eastern — New York (ET)", value: "America/New_York" },
  //   { label: "Central — Chicago (CT)", value: "America/Chicago" },
  //   { label: "Mountain — Denver (MT)", value: "America/Denver" },
  //   { label: "Pacific — Los Angeles (PT)", value: "America/Los_Angeles" },
  // ] as const;

  const [notifState, setNotifState] = useState({
    timezone: "",
    channelEmailEnabled: true,
    channelSmsEnabled: true,
    quietHoursEnabled: false,
    quietHoursStartTime: "",
    quietHoursEndTime: "",
    triageEnabled: true,
    snoozePreset: "" as "" | "1h" | "4h" | "24h" | "custom",
    startDateTimeLocal: "",
    endDateTimeLocal: "",
    clearSnooze: false,
  });
  const quietStartRef = useRef<HTMLInputElement | null>(null);
  const quietEndRef = useRef<HTMLInputElement | null>(null);

  const caretakerTimeZone = useMemo(() => {
    const tz = provider?.timezone;
    return typeof tz === "string" && tz.trim() ? tz.trim() : undefined;
  }, [provider?.timezone]);

  const caretakerTimezoneDisplay = useMemo(
    () => formatOrganizationTimezoneDisplay(caretakerTimeZone),
    [caretakerTimeZone],
  );

  const openNativePicker = (el: HTMLInputElement | null) => {
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
        el.click();
      }
    } catch {
      el.focus();
      el.click();
    }
  };

  const loadCaretakerNotif = async () => {
    if (!id) return;
    try {
      setNotifLoading(true);
      const res: any = await AxiosInstance().get(
        `/notifications/admin/caretakers/${id}/profile`,
      );
      const p = res?.data?.data?.profile ?? res?.data?.data?.preferences ?? res?.data?.data ?? {};
      const ep = p?.eventPreferences ?? {};
      const derivedSnoozePreset =
        p?.snoozePreset ?? p?.lastSnoozePreset ?? "";
      const tz =
        caretakerTimeZone ||
        (typeof p?.timezone === "string" && p.timezone.trim()
          ? p.timezone.trim()
          : undefined);
      setNotifState((prev) => ({
        ...prev,
        timezone: p?.timezone ?? "",
        channelEmailEnabled: p?.channelEmailEnabled ?? true,
        channelSmsEnabled: p?.channelSmsEnabled ?? true,
        quietHoursEnabled: !!p?.quietHoursEnabled,
        quietHoursStartTime: p?.quietHoursStartTime ?? "",
        quietHoursEndTime: p?.quietHoursEndTime ?? "",
        triageEnabled: ep?.TRIAGE_ALERT?.enabled ?? true,
        snoozePreset: derivedSnoozePreset,
        startDateTimeLocal:
          derivedSnoozePreset === "custom"
            ? utcIsoToLocalDateTimeInput(p?.snoozeStartAt, tz)
            : "",
        endDateTimeLocal:
          derivedSnoozePreset === "custom"
            ? utcIsoToLocalDateTimeInput(p?.snoozeUntil, tz)
            : "",
        clearSnooze: false,
      }));
    } catch {
      // GET may not exist in all envs; keep UI editable with defaults.
    } finally {
      setNotifLoading(false);
    }
  };

  const saveCaretakerNotif = async () => {
    if (!id) return;
    try {
      setNotifSaving(true);
      const payload: any = {
        timezone: notifState.timezone || undefined,
        channelEmailEnabled: notifState.channelEmailEnabled,
        channelSmsEnabled: notifState.channelSmsEnabled,
        quietHoursEnabled: notifState.quietHoursEnabled,
        quietHoursStartTime: notifState.quietHoursEnabled
          ? notifState.quietHoursStartTime
          : undefined,
        quietHoursEndTime: notifState.quietHoursEnabled
          ? notifState.quietHoursEndTime
          : undefined,
        eventPreferences: {
          TRIAGE_ALERT: { enabled: !!notifState.triageEnabled },
        },
        snoozePreset: notifState.clearSnooze
          ? undefined
          : notifState.snoozePreset || undefined,
        clearSnooze: notifState.clearSnooze,
      };

      if (notifState.snoozePreset === "custom") {
        const tz = caretakerTimeZone || notifState.timezone || undefined;
        if (isLocalDateTimePastInTimeZone(notifState.startDateTimeLocal, tz)) {
          toast.error("Snooze start cannot be in the past.");
          return;
        }
        if (isLocalDateTimePastInTimeZone(notifState.endDateTimeLocal, tz)) {
          toast.error("Snooze end cannot be in the past.");
          return;
        }
        payload.startDateTime =
          localDateTimeInputToUtcIso(notifState.startDateTimeLocal, tz) ||
          undefined;
        payload.endDateTime =
          localDateTimeInputToUtcIso(notifState.endDateTimeLocal, tz) ||
          undefined;
      }

      await AxiosInstance().patch(
        `/notifications/admin/caretakers/${id}/profile`,
        payload,
      );
      toast.success("Notification settings saved");
      setNotifState((p) => ({ ...p, clearSnooze: false }));
      setNotifOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Failed to save notification settings");
    } finally {
      setNotifSaving(false);
    }
  };


  useGetData(
    "/caretakers/" + id,
    {},
    false,
    !!(id && Object.keys(provider).length == 0),
    (err, data) => {
      if (data && !err) setProvider(data?.data);
    },
  );

        const [filters, setFilters] = useState<Record<string, any>>({});
      
        const filterOptions = useMemo(
          () => [
            {
              label: "Status",
              key: "status",
              isMulti: false,
              values: [
                { label: "Active", key: "active" },
                { label: "Inactive", key: "inactive" },
                { label: "Pending", key: "pending" },
                // { label: "Denied", key: "denied" },
                { label: "Blocked", key: "blocked" },
              ],
            },
          ],
          [],
        );
      
        const providerPatientsView = useMemo(
          () =>
            patients.map((patient: any) => ({
              ...patient,
              ...(patient?.caretakerMinutes ? patient?.caretakerMinutes : {}),
              firstName: `${patient?.firstName || ""} ${
                patient?.middleName || ""
              } ${patient?.lastName || ""}`.trim(),
              organizationId:
                patient?.organizationId?.organizationName || patient?.organizationId,
              assignedProviderId: patient?.assignedProviderId
                ? `${patient?.assignedProviderId?.firstName || ""} ${
                    patient?.assignedProviderId?.middleName || ""
                  } ${patient?.assignedProviderId?.lastName || ""}`.trim()
                : "",
              assignedCaretakerIds: Array.isArray(patient?.assignedCaretakerIds)
                ? patient?.assignedCaretakerIds
                    .map(
                      (caretaker: any) =>
                        `${caretaker?.firstName || ""} ${
                          caretaker?.middleName || ""
                        } ${caretaker?.lastName || ""}`.trim(),
                    )
                    .filter(Boolean)
                    .join(", ")
                : "",
                heightValue: patient?.heightValue?.includes("_")
                  ? `${patient.heightValue.split("_")[0]}' ${patient.heightValue.split("_")[1]}"`
                  : patient?.heightValue,
                facility: patient?.facilityId?.name || "-",
                patientTimezoneDisplay: formatUsTimezoneLabel(patient?.resolvedTimezone),
            })),
          [patients],
        );
  const handlePasswordChange = async (formdata: any) => {
          const {pr, rejectFn, resolveFn} = getPromise()
            getPromiseToast(pr)
    try {
      setLoading(true);
      const {} = await AxiosInstance().post("/auth/admin/reset-password", {
        entityId : provider?._id,
        password : formdata.password,
        role: 'caretaker'
      })
      console.log(formdata);
      setPassword(false);
      resolveFn("Password changed successfully")

    } catch (error) {
      rejectFn(error)

    } finally {
      setLoading(false);
    }
  };
  const handleUpdateCaretaker = async (formData :any, methods :any) => {
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
     
      if (selectedOrgs.length === 0) {
        rejectFn("Please select at least one facility");
        return;
      }

      setLoading(true);
      const updatedCaretaker = {
        // _id: Date.now().toString(),

        firstName: formData?.firstName,
        middleName: formData?.middleName,
        lastName: formData?.lastName,
        dateOfBirth: formData?.dateOfBirth,
        email: formData?.email,
        phone: formData?.phone,
        street: formData?.street,
        city: formData?.city,
        state: formData?.state,
        zipcode: formData?.zipcode,
        gender: formData?.gender,
        status: formData?.status,
        assignedOrganizations: selectedOrgs as any[],
        permissions: formData?.permissions,
        type: formData?.type,
        timezone: formData?.timezone,
        ...(formData?.newCaretakers && formData?.newCaretakers?.length > 0
          ? { newCaretakers: formData?.newCaretakers }
          : {}),
      };

     const {data} :any =  await AxiosInstance().put(
        "/caretakers/" + (provider?._id ?? provider?.id ?? id),
        capitalizeTheStartingCharacterFilter(
          trimmedObject(updatedCaretaker),
          PERSON_CAPITALIZE_FIELDS,
        ),
      );
      console.log("NEW CARETAKER", updatedCaretaker, formData);

setProvider(data?.data)
    
      resolveFn("Caretaker updated successfully");
   setEditOpen(false)
      setSelectedOrgs([]);
    } catch (error :any) {
      rejectFn(error );
      if (methods) {
        if (
          error?.response?.data?.message.includes("email") &&
          error?.response?.data?.message.includes("exist")
        )
          methods.setError("email", {
            message: "Email Already Exists",
          });

        if (
          error?.response?.data?.message.includes("phone") &&
          error?.response?.data?.message.includes("exist")
        )
          methods.setError("phone", {
            message: "Phone Number Already Exists",
          });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      activePage="caretakers"
      entityId={provider?.caretakerId || id}
      rightcomponent={
        <div className="flex items-center gap-2">
          <HoverTooltip
            content="Caretaker history"
            className="bg-gray-200 text-black"
            arrowColor="bg-gray-200 fill-gray-200"
          >
            <Button
              variant="outline"
              size="icon"
              type="button"
              onClick={() => setIsHistoryOpen(true)}
              aria-label="Caretaker history"
            >
              <History className="h-4 w-4" />
            </Button>
          </HoverTooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  setNotifOpen(true);
                  void loadCaretakerNotif();
                }}
              >
                <Bell className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Notification Settings</p>
            </TooltipContent>
          </Tooltip>
        </div>
      }
    >

      <ProfileBanner
        accessModule="canAccessCaretakers"
        updatePassword={() => {
          setPassword(true);
        }}
        profileData={provider}
        isHideNpi={true}
        hideZipCode={false}
        editProfile={(data)=> setEditOpen(true)}
      />

      <TableWithSearch
        clickRow={() => {}}
        columnPreferenceKey={`caretaker-patients-${id}`}
        preferenceTableName="patient-list"
        apiDataKey="patients"
        url="/patients"
        columnHeaders={patientTableHeaders}
        data={patients}
        setData={setPatients}
        fetchCondition={true}
        searchPlaceholder="Search patients ..."
        heading="Patients List"
        isActionColumn
        handleAction={(data) => navigate("/patients/" + data?._id + "/edit")}
        // handleEditAction={(data) => navigate("/patients/" + data?._id + "/edit")}
        isIncludeSearch
        params={{ orgId: selectedOrgId, caretakers: id }}
        viewData={providerPatientsView}
        filtersOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        isColumnSelection
        // isHideSelect
        moreActions={[
          {
            label: "View ",
            icon: <Eye className="w-4 h-4" />,
            onClick: (data: any) => {
              navigate("/patients/" + data?._id);
            },
            isShow: () => true,
          },
          // ...(patientsView === "eligible" ? [{
          //   label: "Enroll Patient",
          //   icon: <Users className="w-4 h-4" />,
          //   onClick: (data: any) => {
          //     setSelectedPatientForEnroll(data);
          //     setOpenEnrollNotes(true);
          //   },
          //   isShow: () => true
          // }] : []),
          //   ...(patientsView === "enrolled" ? [{
          //   label: "Opt Out",
          //   icon: <Users className="w-4 h-4" />,
          //   onClick: (data: any) => {
          //     setSelectedPatientForOptOut(data);
          //     setOpenOptOutForm(true);
          //   },
          //   isShow: (data: any) => data?.status !== "denied"
          // }] : []),
        ]}
      />
      <Dialog open={password} onOpenChange={setPassword}>
        <MyDialogContent className="md:w-md" heading="Update Password">
          <RHFWrapper
            initialValues={{}}
            isValidate={true}
            onSubmit={handlePasswordChange}
            validateSchema={ResetSchema}
            className="space-y-4"
            
          >
            <RhfInput
              name="password"
              label="Password"
              type="password"
              placeholder="Password"
            />
            <RhfInput
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              placeholder="Confirm Password"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >Update</Button>
          </RHFWrapper>
        </MyDialogContent>
      </Dialog>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
             <MyDialogContent heading="Update Caretaker">
                  <CaretakerForm
                                  isEdit={true}
                                  defaultValues={provider}
                                  handleSubmit={handleUpdateCaretaker}
                                  organizations={organizations}
                                  selectedOrgs={selectedOrgs}
                                  setSelectedOrgs={setSelectedOrgs}
                                  setIsDialogOpen={(isOpen :any) => {
                                    setEditOpen(isOpen);
                                    setSelectedOrgs([]);
                                  }}
                                  className="space-y-6"
                                  btnLoading={loading}
                                />

             </MyDialogContent>
      </Dialog>

      <Dialog open={notifOpen} onOpenChange={setNotifOpen}>
        <MyDialogContent heading="Notification Settings">
          {notifLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2 p-3 mt-3">
                {/* <Label>Channel Off</Label> */}
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Email</div>
                  <Switch
                    checked={notifState.channelEmailEnabled}
                    onCheckedChange={(v) =>
                      setNotifState((p) => ({ ...p, channelEmailEnabled: v }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">SMS</div>
                  <Switch
                    checked={notifState.channelSmsEnabled}
                    onCheckedChange={(v) =>
                      setNotifState((p) => ({ ...p, channelSmsEnabled: v }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-2 rounded-md border p-3">
                <Label>Snooze</Label>
                <div className="grid grid-cols-1 gap-3">
                  {/* Timezone — hidden for now; `notifState.timezone` still loads/saves via API */}
                  {/* <div className="space-y-1">
                    <Label>Timezone</Label>
                    <Select
                      value={notifState.timezone || ""}
                      onValueChange={(v) =>
                        setNotifState((p) => ({ ...p, timezone: v }))
                      }
                    >
                      <SelectTrigger className="h-10 w-full">
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[280px] z-[10001]">
                        {CARETAKER_TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div> */}

                  <div className="flex items-center justify-between gap-2">
                    <Select
                      value={notifState.snoozePreset || ""}
                      onValueChange={(v) => {
                        const preset = v as typeof notifState.snoozePreset;
                        const tz = caretakerTimeZone || notifState.timezone;
                        const now = getNowLocalDateTimeInput(tz);
                        setNotifState((p) => ({
                          ...p,
                          snoozePreset: preset,
                          clearSnooze: false,
                          startDateTimeLocal:
                            preset === "custom" && !p.startDateTimeLocal
                              ? now
                              : p.startDateTimeLocal,
                          endDateTimeLocal:
                            preset === "custom" && !p.endDateTimeLocal
                              ? now
                              : p.endDateTimeLocal,
                        }));
                      }}
                    >
                      <SelectTrigger
                        className="h-10 w-full"
                        aria-label="Snooze duration"
                      >
                        <SelectValue placeholder="Select snooze" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1h">1h</SelectItem>
                        <SelectItem value="4h">4h</SelectItem>
                        <SelectItem value="24h">24h</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    {(notifState.snoozePreset ||
    notifState.startDateTimeLocal ||
    notifState.endDateTimeLocal) ? (
    <Button
      onClick={() =>
        setNotifState((p) => ({
          ...p,
          snoozePreset: "",
          startDateTimeLocal: "",
          endDateTimeLocal: "",
          clearSnooze: true,
        }))
      }
      // className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
      aria-label="Clear"
    >
      Clear
    </Button>
  ) : null}
                  </div>
                </div>
                {notifState.snoozePreset === "custom" ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label>Start</Label>
                        <SnoozeCustomDateTimeField
                          id="caretaker-snooze-start"
                          value={notifState.startDateTimeLocal}
                          timeZone={caretakerTimeZone || notifState.timezone}
                          disablePast
                          onChange={(v) =>
                            setNotifState((p) => ({
                              ...p,
                              startDateTimeLocal: v,
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>End</Label>
                        <SnoozeCustomDateTimeField
                          id="caretaker-snooze-end"
                          value={notifState.endDateTimeLocal}
                          timeZone={caretakerTimeZone || notifState.timezone}
                          disablePast
                          onChange={(v) =>
                            setNotifState((p) => ({
                              ...p,
                              endDateTimeLocal: v,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 font-bold text-xs text-muted-foreground">
                      <span>
                        Please select the time as per caretaker&apos;s timezone{" "}
                        <span className="font-medium text-green-600">
                          {caretakerTimezoneDisplay ||
                            caretakerTimeZone ||
                            notifState.timezone ||
                            "—"}
                        </span>
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-2 rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <Label>Quiet Hours</Label>
                  <Switch
                    checked={notifState.quietHoursEnabled}
                    onCheckedChange={(v) =>
                      setNotifState((p) => ({ ...p, quietHoursEnabled: v }))
                    }
                  />
                </div>
                {notifState.quietHoursEnabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Start</Label>
                      <div
                        className="relative h-10 w-full cursor-pointer rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                        onClick={() => openNativePicker(quietStartRef.current)}
                      >
                        <div className="pointer-events-none absolute inset-0 z-0 flex items-center px-3 text-sm">
                          {notifState.quietHoursStartTime ? (
                            formatHHmmAs12h(notifState.quietHoursStartTime)
                          ) : (
                            <span className="text-muted-foreground">
                              Select time
                            </span>
                          )}
                        </div>
                        <input
                          ref={quietStartRef}
                          type="time"
                          className="absolute inset-0 z-1 h-full w-full cursor-pointer opacity-0 date-input-native"
                          value={notifState.quietHoursStartTime}
                          onClick={() =>
                            openNativePicker(quietStartRef.current)
                          }
                          onFocus={() =>
                            openNativePicker(quietStartRef.current)
                          }
                          onChange={(e) =>
                            setNotifState((p) => ({
                              ...p,
                              quietHoursStartTime: e.target.value,
                            }))
                          }
                          aria-label="Quiet hours start"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label>End</Label>
                      <div
                        className="relative h-10 w-full cursor-pointer rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                        onClick={() => openNativePicker(quietEndRef.current)}
                      >
                        <div className="pointer-events-none absolute inset-0 z-0 flex items-center px-3 text-sm">
                          {notifState.quietHoursEndTime ? (
                            formatHHmmAs12h(notifState.quietHoursEndTime)
                          ) : (
                            <span className="text-muted-foreground">
                              Select time
                            </span>
                          )}
                        </div>
                        <input
                          ref={quietEndRef}
                          type="time"
                          className="absolute inset-0 z-1 h-full w-full cursor-pointer opacity-0 date-input-native"
                          value={notifState.quietHoursEndTime}
                          onClick={() => openNativePicker(quietEndRef.current)}
                          onFocus={() => openNativePicker(quietEndRef.current)}
                          onChange={(e) =>
                            setNotifState((p) => ({
                              ...p,
                              quietHoursEndTime: e.target.value,
                            }))
                          }
                          aria-label="Quiet hours end"
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-2 rounded-md border p-3">
                {/* <Label>Triage</Label> */}
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Triage Alert</div>
                  <Switch
                    checked={notifState.triageEnabled}
                    onCheckedChange={(v) =>
                      setNotifState((p) => ({ ...p, triageEnabled: v }))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setNotifOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={saveCaretakerNotif} disabled={notifSaving}>
                  {notifSaving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          )}
        </MyDialogContent>
      </Dialog>

      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <MyDialogContent
          heading="Caretaker history"
          className="!h-[min(720px,85vh)] !w-[min(42rem,96vw)] !max-w-[42rem] [&>div]:!p-0 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-5 [&_[data-slot=dialog-header]]:shrink-0 [&_[data-slot=dialog-header]]:border-b [&_[data-slot=dialog-header]]:border-[#E5E7EB] [&_[data-slot=dialog-header]]:!px-5 [&_[data-slot=dialog-header]]:!py-4 [&_[data-slot=dialog-title]]:pr-8 [&_[data-slot=dialog-title]]:text-base [&_[data-slot=dialog-title]]:font-semibold [&_[data-slot=dialog-title]]:text-[#111827] [&>div:last-child]:!flex [&>div:last-child]:!min-h-0 [&>div:last-child]:!flex-1 [&>div:last-child]:!flex-col [&>div:last-child]:!overflow-hidden [&>div:last-child]:!p-0 [&>div:last-child]:px-5 [&>div:last-child]:pb-4"
        >
          <PatientHistoryTimeline
            caretakerId={provider?._id ?? provider?.id ?? id}
            enabled={isHistoryOpen}
          />
        </MyDialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CaretakerProfile;
